// CORE-REVERT (core plan item 7, docs/direction/2026-11-03-core-delivery-plan.md §1 row 7, lane C
// days 4–6): one-click revert of a bad move — an incident's (a freeze) or a single agent run's.
// Behind the company switch `settings.flags.core_revert` (off by default: these routes answer 409
// `conflict` while it is off).
//
//   GET  incidents/{id}/revert     managers: the incident's receipt — its runs, outbox rows and
//                                  agent writes, each with what a revert does to it
//   POST incidents/{id}/revert     managers: revert it, in one action
//   GET  runs/{id}/revert          managers: the same for one agent run
//   POST runs/{id}/revert          managers: revert the run (opens its `reversal` incident)
//
// A revert, in the request transaction: the queued sends are cancelled (`outbox.cancelled`); the
// agents' writes still within their 15-minute undo window are undone through their undo records
// (`task.undone`, then the compensation's own event); a delivered send (or one that may have gone
// out) is marked irreversible, and with any write that could not be undone it goes into one
// follow-up task for a person to handle by hand. `incident.reverted` lists every item and its
// outcome, with the agent it belongs to: the decision ledger counts each as a reversal.
import { z } from "zod";
import { ExternalActionKind, OutboxStatus } from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { CompanyItemParams } from "./common";
import { IncidentView } from "./incidents";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

/** An agent write the revert can reach (an undo record). */
export const RevertWrite = z
  .object({
    undo_id: Uuid,
    /** The compensation's kind (`task.status`, `task.patch`, `task.field`, `task.move`). */
    kind: z.string(),
    /** The write's event (the trail's line it undoes). */
    event_id: Uuid.nullable(),
    subject: z.object({ kind: z.string(), id: Uuid }),
    actor_kind: z.string(),
    membership_id: Uuid.nullable(),
    run_id: Uuid.nullable(),
    created_at: Timestamp,
    expires_at: Timestamp,
    /** `undoable` within its window; `undone` already; `expired` past it (handled by hand). */
    state: z.enum(["undoable", "undone", "expired"]),
  })
  .meta({ id: "RevertWrite" });

/** An outward send the revert can reach (an outbox row). */
export const RevertSend = z
  .object({
    outbox_id: Uuid,
    action: ExternalActionKind,
    status: OutboxStatus,
    approval_id: Uuid.nullable(),
    membership_id: Uuid.nullable(),
    run_id: Uuid.nullable(),
    sent_at: Timestamp.nullable(),
    provider_message_id: z.string().nullable(),
    /**
     * `cancellable` (queued, failed or a dead letter); `cancelled` already; `irreversible`: sent,
     * being sent, or its delivery unresolved (it may have gone out; a queued one is cancelled too).
     */
    state: z.enum(["cancellable", "cancelled", "irreversible"]),
  })
  .meta({ id: "RevertSend" });

export const RevertRun = z
  .object({ run_id: Uuid, membership_id: Uuid.nullable(), state: z.string() })
  .meta({ id: "RevertRun" });

/** The receipt of an incident or a run, and what a revert does to each item. */
export const RevertList = z
  .object({
    target: z.object({ kind: z.enum(["incident", "run"]), id: Uuid }),
    /** The incident (a run's `reversal` incident once it was reverted; null before). */
    incident_id: Uuid.nullable(),
    membership_id: Uuid.nullable(),
    runs: z.array(RevertRun),
    sends: z.array(RevertSend),
    writes: z.array(RevertWrite),
    /** The follow-up task a revert filed for what is handled by hand (null: none yet). */
    follow_up_task_id: Uuid.nullable(),
  })
  .meta({ id: "RevertList" });
export type RevertList = z.infer<typeof RevertList>;

export const RevertBody = z
  .strictObject({ reason: z.string().trim().max(500).optional() })
  .meta({ id: "RevertBody" });

/** One item a revert reached, and what became of it. */
export const RevertItem = z
  .object({
    kind: z.enum(["write", "send"]),
    /** The undo record (a write) or the outbox row (a send). */
    id: Uuid,
    outcome: z.enum(["undone", "cancelled", "irreversible", "expired", "failed"]),
    /** Why it failed (a write changed since, say); null otherwise. */
    detail: z.string().nullable(),
    membership_id: Uuid.nullable(),
    run_id: Uuid.nullable(),
  })
  .meta({ id: "RevertItem" });
export type RevertItem = z.infer<typeof RevertItem>;

export const RevertResult = z
  .object({
    incident: IncidentView,
    items: z.array(RevertItem),
    follow_up_task_id: Uuid.nullable(),
    list: RevertList,
  })
  .meta({ id: "RevertResult" });

const revertEmits = [
  "incident.reverted",
  "outbox.cancelled",
  "approval.executed",
  "wakeup.requested",
  "task.undone",
  "task.status_changed",
  "task.updated",
  "task.due_changed",
  "task.priority_changed",
  "task.field_changed",
  "task.moved",
  "task.created",
  "page.created",
] as const;

export const revertRoutes = {
  "incidents.revertList": defineRoute({
    ...core,
    id: "incidents.revertList",
    method: "get",
    path: `${base}/incidents/{id}/revert`,
    tag: "incidents",
    summary:
      "An incident's receipt: its runs, outbox rows and agent writes, each with what a revert does to it",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: RevertList },
    errors: ["conflict"],
    emits: [],
  }),
  "incidents.revert": defineRoute({
    ...core,
    id: "incidents.revert",
    method: "post",
    path: `${base}/incidents/{id}/revert`,
    tag: "incidents",
    summary:
      "Revert an incident in one action: queued sends cancelled, undoable writes undone, delivered sends marked irreversible with a follow-up task",
    access: "manager",
    params: CompanyItemParams,
    body: RevertBody,
    response: { status: 200, schema: RevertResult },
    errors: ["conflict"],
    emits: [...revertEmits],
  }),
  "runs.revertList": defineRoute({
    ...core,
    id: "runs.revertList",
    method: "get",
    path: `${base}/runs/{id}/revert`,
    tag: "incidents",
    summary: "One agent run's receipt: its outbox rows and writes, with what a revert does to each",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: RevertList },
    errors: ["conflict"],
    emits: [],
  }),
  "runs.revert": defineRoute({
    ...core,
    id: "runs.revert",
    method: "post",
    path: `${base}/runs/{id}/revert`,
    tag: "incidents",
    summary:
      "Revert one agent run in one action (its reversal incident opened): sends cancelled, writes undone, delivered sends irreversible",
    access: "manager",
    params: CompanyItemParams,
    body: RevertBody,
    response: { status: 200, schema: RevertResult },
    errors: ["conflict"],
    emits: ["incident.opened", ...revertEmits],
  }),
};
